import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid,
  LinearProgress, Paper, Stack, Tab, Tabs, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import DownloadIcon from '@mui/icons-material/Download';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PrintIcon from '@mui/icons-material/Print';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { toast } from 'react-toastify';
import { api, useAuth } from '../services/AuthService';
import BrowserPrintService from '../services/BrowserPrintService';

const statusCards = [
  ['new', 'Pedidos novos', '#2563eb'],
  ['preparing', 'Preparando', '#f59e0b'],
  ['ready', 'Prontos', '#16a34a'],
  ['delayed', 'Atrasados', '#dc2626']
];

const playAlert = () => {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) throw new Error('Som nao suportado neste navegador');
  const context = new AudioContext();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.frequency.value = 880;
  gain.gain.setValueAtTime(0.18, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.7);
  oscillator.connect(gain); gain.connect(context.destination);
  oscillator.start(); oscillator.stop(context.currentTime + 0.7);
};

function StatusDot({ ok, label, detail }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3, minWidth: 180 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: ok ? '#16a34a' : '#dc2626' }} />
        <Box><Typography fontWeight={800}>{label}</Typography><Typography variant="caption" color="text.secondary">{detail}</Typography></Box>
      </Stack>
    </Paper>
  );
}

export function OperationalSummary({ compact = false }) {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const previousDelayed = useRef(0);

  const load = useCallback(async () => {
    if (!user?.lanchonete_id) return setLoading(false);
    try {
      const { data } = await api.get('/operations/overview');
      setOverview(data);
      if (data.counts.delayed > previousDelayed.current && previousDelayed.current >= 0) playAlert();
      previousDelayed.current = data.counts.delayed;
    } catch (error) {
      console.error('Painel operacional:', error);
    } finally { setLoading(false); }
  }, [user?.lanchonete_id]);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => { if (!document.hidden) load(); }, 30000);
    return () => window.clearInterval(timer);
  }, [load]);

  if (!user?.lanchonete_id) return null;
  if (loading) return <LinearProgress sx={{ mb: 2 }} />;
  return (
    <Box sx={{ mb: compact ? 2 : 3 }}>
      <Grid container spacing={2}>
        {statusCards.map(([key, label, color]) => (
          <Grid item xs={6} md={3} key={key}>
            <Card sx={{ borderLeft: `6px solid ${color}`, bgcolor: key === 'delayed' && overview?.counts?.[key] ? '#fff1f2' : '#fff' }}>
              <CardContent sx={{ py: compact ? 1.5 : 2.5, '&:last-child': { pb: compact ? 1.5 : 2.5 } }}>
                <Typography color="text.secondary" fontWeight={700}>{label}</Typography>
                <Typography variant="h3" fontWeight={900} color={color}>{overview?.counts?.[key] || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {overview?.delayed?.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <strong>{overview.delayed.length} pedido(s) precisam de atencao.</strong>{' '}
          {overview.delayed.slice(0, 4).map((order) => `#${order.id} (${Math.round(order.age_minutes)} min)`).join(', ')}
        </Alert>
      )}
    </Box>
  );
}

export default function OperationsCenter() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [health, setHealth] = useState(null);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);
  const [testing, setTesting] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [healthResponse, reportsResponse] = await Promise.all([
        api.get('/operations/health'), api.get('/operations/reports?days=30')
      ]);
      setHealth(healthResponse.data); setReports(reportsResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nao foi possivel consultar a saude do sistema');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const on = () => setOnline(true); const off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const runTest = async (type) => {
    if (testing) return;
    setTesting(type);
    try {
      if (type === 'sound') { playAlert(); toast.success('Som reproduzido'); }
      if (type === 'notification') {
        if (!('Notification' in window)) throw new Error('Navegador sem suporte a notificacoes');
        const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
        if (permission !== 'granted') throw new Error('Permissao de notificacao nao concedida');
        new Notification('All Delivery', { body: 'Teste de notificacao realizado com sucesso.' });
        toast.success('Notificacao enviada');
      }
      if (type === 'printer') {
        const result = await BrowserPrintService.testPrint();
        if (result === false) throw new Error('A impressora nao confirmou o teste');
        toast.success('Teste enviado para a impressora');
      }
    } catch (error) {
      toast.error(error.message || 'Falha no teste');
      api.post('/operations/client-event', { category: type === 'printer' ? 'impressao' : 'notificacao', severity: 'error', message: error.message }).catch(() => {});
    } finally { setTesting(''); }
  };

  const runAllTests = async () => {
    if (testing) return;
    for (const type of ['sound', 'notification', 'printer']) {
      await runTest(type);
    }
  };

  const backup = async () => {
    try {
      const response = await api.get('/operations/backup', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data); const link = document.createElement('a');
      link.href = url; link.download = `all-delivery-backup-${user?.lanchonete_id}-${new Date().toISOString().slice(0,10)}.json`;
      link.click(); URL.revokeObjectURL(url);
    } catch (_) { toast.error('Nao foi possivel baixar o backup'); }
  };

  const sortedHours = useMemo(() => [...(reports?.peakHours || [])].sort((a, b) => b.orders - a.orders), [reports]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} mb={2}>
        <Box><Typography variant="h4" fontWeight={900}>Central de operacao</Typography><Typography color="text.secondary">Pedidos, testes, conexoes, falhas e relatorios em um so lugar.</Typography></Box>
        <Stack direction="row" spacing={1}><Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Atualizar</Button><Button startIcon={<DownloadIcon />} variant="outlined" onClick={backup}>Exportar backup</Button></Stack>
      </Stack>
      <OperationalSummary />
      <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" sx={{ mb: 2 }}>
        <Tab label="Saude e testes" /><Tab label="Falhas recentes" /><Tab label="Relatorios" />
      </Tabs>
      {loading ? <CircularProgress /> : tab === 0 ? (
        <Stack spacing={2}>
          <Paper sx={{ p: 2.5, borderRadius: 4 }}>
            <Typography variant="h6" fontWeight={900} mb={2}>Conexoes</Typography>
            <Stack direction="row" gap={1.5} flexWrap="wrap">
              <StatusDot ok={online} label="Internet" detail={online ? 'Este computador esta online' : 'Sem conexao neste computador'} />
              <StatusDot ok={Boolean(health?.server?.connected)} label="Servidor" detail={health?.server?.connected ? `${health.server.latencyMs} ms` : 'Indisponivel'} />
              <StatusDot ok={Boolean(health?.database?.connected)} label="Banco" detail={health?.database?.connected ? 'Respondendo' : 'Indisponivel'} />
              <StatusDot ok={Boolean(health?.printing?.configured)} label="Impressora" detail={health?.printing?.configured ? 'Configurada; use o teste fisico' : 'Nao configurada'} />
              <StatusDot ok={Boolean(health?.whatsapp?.connected)} label="WhatsApp" detail={health?.whatsapp?.status || 'Desconectado'} />
            </Stack>
          </Paper>
          <Paper sx={{ p: 2.5, borderRadius: 4 }}>
            <Typography variant="h6" fontWeight={900}>Teste geral</Typography>
            <Typography color="text.secondary" mb={2}>Execute os tres testes neste computador antes de iniciar o atendimento.</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button color="success" variant="contained" startIcon={<HealthAndSafetyIcon />} onClick={runAllTests} disabled={Boolean(testing)}>Testar tudo</Button>
              <Button variant="contained" startIcon={<VolumeUpIcon />} onClick={() => runTest('sound')} disabled={Boolean(testing)}>Testar som</Button>
              <Button variant="contained" startIcon={<NotificationsActiveIcon />} onClick={() => runTest('notification')} disabled={Boolean(testing)}>Testar notificacao</Button>
              <Button variant="contained" startIcon={<PrintIcon />} onClick={() => runTest('printer')} disabled={Boolean(testing)}>Testar impressora</Button>
            </Stack>
          </Paper>
        </Stack>
      ) : tab === 1 ? (
        <TableContainer component={Paper}><Table size="small"><TableHead><TableRow><TableCell>Horario</TableCell><TableCell>Area</TableCell><TableCell>Problema</TableCell><TableCell>Situacao</TableCell></TableRow></TableHead><TableBody>
          {(health?.events || []).length === 0 ? <TableRow><TableCell colSpan={4}>Nenhuma falha recente registrada desde a ultima inicializacao do servidor.</TableCell></TableRow> : health.events.map((event) => <TableRow key={event.id}><TableCell>{new Date(event.timestamp).toLocaleString('pt-BR')}</TableCell><TableCell><Chip size="small" label={event.category} /></TableCell><TableCell>{event.message}</TableCell><TableCell><Chip size="small" color={event.severity === 'error' ? 'error' : 'warning'} label={event.statusCode || event.severity} /></TableCell></TableRow>)}
        </TableBody></Table></TableContainer>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}><Card><CardContent><Typography color="text.secondary">Faturamento (30 dias)</Typography><Typography variant="h4" fontWeight={900}>R$ {Number(reports?.summary?.revenue || 0).toFixed(2)}</Typography><Typography>{reports?.summary?.orders || 0} pedidos · {reports?.summary?.cancelled || 0} cancelados</Typography></CardContent></Card></Grid>
          <Grid item xs={12} md={8}><TableContainer component={Paper}><Typography variant="h6" fontWeight={900} p={2}>Produtos mais vendidos</Typography><Table size="small"><TableHead><TableRow><TableCell>Produto</TableCell><TableCell>Quantidade</TableCell><TableCell>Faturamento</TableCell></TableRow></TableHead><TableBody>{(reports?.products || []).map((item) => <TableRow key={item.name}><TableCell>{item.name}</TableCell><TableCell>{item.quantity}</TableCell><TableCell>R$ {Number(item.revenue || 0).toFixed(2)}</TableCell></TableRow>)}</TableBody></Table></TableContainer></Grid>
          <Grid item xs={12}><Paper sx={{ p: 2 }}><Typography variant="h6" fontWeight={900}>Horarios de pico</Typography><Stack direction="row" gap={1} flexWrap="wrap" mt={1}>{sortedHours.map((item) => <Chip key={item.hour} label={`${String(item.hour).padStart(2,'0')}:00 · ${item.orders} pedidos`} color={item === sortedHours[0] ? 'error' : 'default'} />)}</Stack></Paper></Grid>
        </Grid>
      )}
    </Box>
  );
}
